import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TablePagination,
    TextField,
    Container,
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment
} from "@mui/material";
import { Edit, Delete, Add, Search } from "@mui/icons-material";
import ClassService from "~/admin/services/ClassServices";
import DepartmentService from "~/admin/services/DepartmentServices";

const ClassComp = () => {
    const [classes, setClasses] = useState([]);
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [search, setSearch] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalCount, setTotalCount] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        departmentID: ""
    });
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [errors, setErrors] = useState({
        name: false,
        departmentID: false
    });
    const [helperTexts, setHelperTexts] = useState({
        name: "",
        departmentID: ""
    });

    const [debouncedSearch, setDebouncedSearch] = useState(search);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchClasses();
        fetchDepartments();
    }, [page, rowsPerPage, debouncedSearch, departmentFilter]);

    const fetchClasses = async () => {
        try {
            const departmentId = departmentFilter || null;
            const response = await ClassService.getAll(
                true,
                rowsPerPage,
                page + 1,
                debouncedSearch,
                departmentId
            );
            setClasses(response.items);
            setTotalCount(response.totalCount);
            setFilteredClasses(response.items);
        } catch (error) {
            showAlert("Lỗi khi tải danh sách lớp!", "error");
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await DepartmentService.getAll(false);
            setDepartments(response.items);
        } catch (error) {
            showAlert("Lỗi khi tải danh sách khoa!", "error");
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const handlePageChange = (event, newPage) => setPage(newPage);
    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleModalOpen = () => setIsModalOpen(true);
    const handleModalClose = () => {
        setIsModalOpen(false);
        setFormData({
            name: "",
            departmentID: ""
        });
        setIsEditing(false);
        setEditingId(null);
        setErrors({
            name: false,
            departmentID: false
        });
        setHelperTexts({
            name: "",
            departmentID: ""
        });
    };

    const handleEdit = async (classId) => {
        try {
            const classData = await ClassService.getById(classId);
            setIsEditing(true);
            setEditingId(classData.id);
            setFormData({
                name: classData.name || "",
                departmentID: classData.departmentID || ""
            });
            handleModalOpen();
        } catch (error) {
            showAlert("Lỗi khi tải danh sách lớp!", "error");
        }
    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        if (event.target.value.trim()) {
            setErrors(prev => ({ ...prev, name: false }));
            setHelperTexts(prev => ({ ...prev, name: "" }));
        }
    };

    const handleDepartmentFilterChange = (event) => {
        setDepartmentFilter(event.target.value);
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = { ...errors };
        const newHelperTexts = { ...helperTexts };

        if (!formData.name.trim()) {
            newErrors.name = true;
            newHelperTexts.name = "Tên lớp không được bỏ trống!";
            isValid = false;
        } else {
            newErrors.name = false;
            newHelperTexts.name = "";
        }

        if (!formData.departmentID) {
            newErrors.departmentID = true;
            newHelperTexts.departmentID = "Tên khoa không được bỏ trống!";
            isValid = false;
        } else {
            newErrors.departmentID = false;
            newHelperTexts.departmentID = "";
        }

        setErrors(newErrors);
        setHelperTexts(newHelperTexts);
        return isValid;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;

        try {
            let response = "";
            if (isEditing) {
                response = await ClassService.update({
                    id: editingId,
                    name: formData.name,
                    departmentID: formData.departmentID
                });
                showAlert("Cập nhật lớp thành công!", "success");
            } else {
                response = await ClassService.create(formData);
                showAlert("Thêm lớp thành công!", "success");
            }

            if (response && response.success === false) {
                showAlert(response.message, "warning");
                return;
            }

            fetchClasses();
            handleModalClose();
        } catch (error) {
            showAlert("Lỗi khi lưu thông tin lớp!", "error");
        }
    };

    const handleDeleteDialogOpen = (id) => {
        setDeletingId(id);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false);
        setDeletingId(null);
    };

    const handleDelete = async () => {
        try {
            let response = await ClassService.delete(deletingId);
            if (response && response.success === false) {
                showAlert(response.message, "warning");
                return;
            } else {
                showAlert("Xóa lớp thành công!", "success");
            }
            fetchClasses();
            handleDeleteDialogClose();
        } catch (error) {
            showAlert("Lỗi! Xóa lớp không thành công!", "error");
        }
    };

    return (
        <Container>
            <Snackbar
                open={alert.open}
                autoHideDuration={3000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>
                    {alert.message}
                </Alert>
            </Snackbar>

            <Box sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: 'wrap',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                    <TextField
                        label="Tìm kiếm"
                        variant="outlined"
                        value={search}
                        onChange={handleSearchChange}
                        sx={{ width: "50%", marginRight: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Tên khoa</InputLabel>
                        <Select
                            value={departmentFilter}
                            onChange={handleDepartmentFilterChange}
                            label="Tên khoa"
                        >
                            <MenuItem value="">Tất cả</MenuItem>
                            {departments.map((dept) => (
                                <MenuItem key={dept.id} value={dept.id}>
                                    {dept.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<Add />}
                    onClick={handleModalOpen}
                >
                    THÊM LỚP
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Tên lớp</strong></TableCell>
                            <TableCell><strong>Tên khoa</strong></TableCell>
                            <TableCell><strong>Hành động</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredClasses.map((cls) => (
                            <TableRow key={cls.id}>
                                <TableCell>{cls.name}</TableCell>
                                <TableCell>
                                    {departments.find(d => d.id === cls.departmentID)?.name || 'N/A'}
                                </TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={() => handleEdit(cls.id)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="secondary"
                                        onClick={() => handleDeleteDialogOpen(cls.id)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 100, 200, 300, 500]}
            />

            <Dialog open={isModalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
                <DialogTitle><strong>{isEditing ? "Cập nhật lớp" : "Thêm lớp"}</strong></DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label={
                            <span>Tên lớp <span style={{ color: "red" }}>*</span></span>
                        }
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={formData.name}
                        onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            if (e.target.value.trim()) {
                                setErrors(prev => ({ ...prev, name: false }));
                                setHelperTexts(prev => ({ ...prev, name: "" }));
                            }
                        }}
                        error={errors.name}
                        helperText={helperTexts.name}
                    />
                    <FormControl fullWidth margin="normal" error={errors.departmentID}>
                        <InputLabel>
                            <span>Tên khoa <span style={{ color: "red" }}>*</span></span>
                        </InputLabel>
                        <Select
                            value={formData.departmentID}
                            onChange={(e) => {
                                setFormData({ ...formData, departmentID: e.target.value });
                                if (e.target.value) {
                                    setErrors(prev => ({ ...prev, departmentID: false }));
                                    setHelperTexts(prev => ({ ...prev, departmentID: "" }));
                                }
                            }}
                            label="Tên khoa *"
                        >
                            {departments.map((dept) => (
                                <MenuItem key={dept.id} value={dept.id}>
                                    {dept.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.departmentID && (
                            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                                {helperTexts.departmentID}
                            </Box>
                        )}
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleModalClose} color="primary">HỦY</Button>
                    <Button
                        variant="contained"
                        onClick={handleFormSubmit}
                        color="primary"
                    >
                        {isEditing ? "CẬP NHẬT" : "THÊM"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle><strong>Xác nhận xóa</strong></DialogTitle>
                <DialogContent dividers>
                    <p>Bạn có chắc muốn xóa lớp này?</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="secondary">HỦY</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">XÓA</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClassComp;